import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Truck, Ruler, Mail, Phone, MapPin, ShieldCheck, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const PARISH_SHIPPING = [
  { zone: "Knutsford Express (Island-wide)", detail: "Drop-off at nearest Knutsford Express location · 1-2 business days · J$500", pickup: false },
  { zone: "ZipMail (To Post Office)", detail: "Collect at your nearest Post Office · 2-4 business days · J$500", pickup: false },
];

const SIZE_GUIDE = [
  { size: "S", chest: '18"', length: '27"' },
  { size: "M", chest: '20"', length: '28"' },
  { size: "L", chest: '22"', length: '29"' },
  { size: "XL", chest: '24"', length: '30"' },
  { size: "XXL", chest: '26"', length: '31"' },
];

const IG_FEED = [
  "https://static.prod-images.emergentagent.com/jobs/7d4b1824-6f94-4ced-ae91-4ba211ce6db0/images/ddba05562e12d6cac5f0357cd7f2bb652d13da7462eb00c61901227d09344ed7.jpeg",
  "/brand/greatness-green-flat.jpg",
  "/brand/greatness-gold-flat.jpg",
  "/brand/dat-is-it-back.jpg",
  "/brand/lion-rebel-black.jpg",
  "/brand/greatness-pair.jpg",
];

export function Footer() {
  const [shipOpen, setShipOpen] = useState(false);
  const [sizeOpen, setSizeOpen] = useState(false);

  return (
    <footer data-testid="site-footer" className="border-t border-white/10 bg-black mt-0">
      {/* Instagram feed */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <a
            href="https://instagram.com/turahtid"
            target="_blank"
            rel="noreferrer"
            data-testid="ig-feed-heading"
            className="inline-flex items-center gap-2 font-heading font-bold uppercase text-sm hover:text-jamaica-gold mb-4"
          >
            <Instagram className="w-4 h-4" /> @turahtid
          </a>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {IG_FEED.map((img, i) => (
              <a
                key={i}
                href="https://instagram.com/turahtid"
                target="_blank"
                rel="noreferrer"
                data-testid={`ig-post-${i}`}
                className="group relative aspect-square overflow-hidden bg-zinc-900 border border-white/10"
              >
                <img src={img} alt="Turahtid on the street" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                  <Instagram className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
        <div className="col-span-2 md:col-span-1">
          <img src="/brand/logo-full.webp" alt="Turahtid — True to the Culture" data-testid="footer-logo" loading="lazy" className="w-32 h-auto rounded-sm" />
          <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mt-2">
            Talented · Unique · Rebel
          </p>
          <p className="text-sm text-zinc-400 mt-3 max-w-xs">
            Authentic Jamaican patois streetwear. Printed with pride in Kingston.
          </p>
        </div>
        <div>
          <h4 className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-3">Quick Navigation</h4>
          <ul className="space-y-2 text-sm text-zinc-300">
            <li><Link to="/shop" data-testid="footer-shop" className="hover:text-jamaica-gold">Shop</Link></li>
            <li><Link to="/about" data-testid="footer-about" className="hover:text-jamaica-gold">About Us</Link></li>
            <li><Link to="/privacy-policy" data-testid="footer-privacy" className="hover:text-jamaica-gold">Privacy Policy</Link></li>
            <li><Link to="/terms-and-conditions" data-testid="footer-terms" className="hover:text-jamaica-gold">Terms &amp; Conditions</Link></li>
            <li><Link to="/refund-policy" data-testid="footer-refund" className="hover:text-jamaica-gold">Refund &amp; Exchange Policy</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-3">Contact</h4>
          <ul className="space-y-2.5 text-sm text-zinc-300">
            <li className="flex items-start gap-2"><MapPin className="w-4 h-4 text-jamaica-gold mt-0.5 shrink-0" /> <span>May Pen, Clarendon, Jamaica</span></li>
            <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-jamaica-gold shrink-0" /> <a href="mailto:talenteduniquerebel@gmail.com" data-testid="footer-email" className="hover:text-jamaica-gold break-all">talenteduniquerebel@gmail.com</a></li>
            <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-jamaica-gold shrink-0" /> <a href="tel:+18763539081" data-testid="footer-phone" className="hover:text-jamaica-gold">+1 (876) 353-9081</a></li>
          </ul>
        </div>
        <div>
          <h4 className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-3">Help</h4>
          <ul className="space-y-2 text-sm text-zinc-300">
            <li><button data-testid="footer-shipping" onClick={() => setShipOpen(true)} className="hover:text-jamaica-gold inline-flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> Shipping &amp; Delivery</button></li>
            <li><button data-testid="footer-sizeguide" onClick={() => setSizeOpen(true)} className="hover:text-jamaica-gold inline-flex items-center gap-1.5"><Ruler className="w-3.5 h-3.5" /> Size Guide</button></li>
            <li><Link to="/track" className="hover:text-jamaica-gold">Track My Order</Link></li>
            <li><a href="https://instagram.com/turahtid" target="_blank" rel="noreferrer" className="hover:text-jamaica-gold inline-flex items-center gap-1.5"><Instagram className="w-3.5 h-3.5" /> @turahtid</a></li>
          </ul>
        </div>
      </div>

      {/* Payments, security & currency */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div data-testid="payment-badges" className="flex flex-wrap items-center gap-2.5">
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Secure Payments</span>
            <span className="px-2.5 py-1 border border-white/15 bg-white/5 font-heading font-bold italic text-xs tracking-wide text-white">VISA</span>
            <span className="px-2.5 py-1 border border-white/15 bg-white/5 font-heading font-bold text-xs tracking-wide text-white">Mastercard</span>
            <span className="px-2.5 py-1 border border-jamaica-gold/40 bg-jamaica-gold/5 font-mono text-[10px] uppercase tracking-widest text-jamaica-gold">Sagicor Gateway</span>
            <span className="px-2.5 py-1 border border-jamaica-green/40 bg-jamaica-green/5 font-mono text-[10px] uppercase tracking-widest text-jamaica-green inline-flex items-center gap-1"><Lock className="w-3 h-3" /> 256-bit SSL</span>
            <span className="px-2.5 py-1 border border-jamaica-green/40 bg-jamaica-green/5 font-mono text-[10px] uppercase tracking-widest text-jamaica-green inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> PCI-DSS Compliant</span>
          </div>
          <p data-testid="currency-notice" className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">
            All prices listed in JMD (Jamaican Dollars) and USD.
          </p>
        </div>
      </div>

      <div className="border-t border-white/10 py-4 text-center">
        <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">
          © {new Date().getFullYear()} Turahtid Clothing · Out ah many, one
        </p>
      </div>

      {/* Shipping dialog */}
      <Dialog open={shipOpen} onOpenChange={setShipOpen}>
        <DialogContent className="bg-popover border-border rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-wide">SHIPPING ACROSS JA</DialogTitle>
            <DialogDescription className="text-zinc-400">Two flat-rate options island-wide, both J$500.</DialogDescription>
          </DialogHeader>
          <div className="divide-y divide-white/5">
            {PARISH_SHIPPING.map((z) => (
              <div key={z.zone} className="py-3">
                <p className="font-heading font-bold uppercase text-sm">{z.zone}</p>
                <p className="font-mono text-xs text-zinc-400 mt-1">{z.detail}{z.pickup ? " · free pickup available" : ""}</p>
              </div>
            ))}
          </div>
          <p className="font-mono text-[11px] text-zinc-500 uppercase tracking-widest">Tracking sent after payment &amp; dispatch.</p>
        </DialogContent>
      </Dialog>

      {/* Size guide dialog */}
      <Dialog open={sizeOpen} onOpenChange={setSizeOpen}>
        <DialogContent className="bg-popover border-border rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-wide">SIZE GUIDE</DialogTitle>
            <DialogDescription className="text-zinc-400">Measurements are garment flat-lay, in inches. Fits run true — size up for boxy.</DialogDescription>
          </DialogHeader>
          <table className="w-full font-mono text-sm">
            <thead>
              <tr className="text-zinc-500 uppercase text-[10px] tracking-widest border-b border-white/10">
                <th className="text-left py-2">Size</th>
                <th className="text-left py-2">Chest</th>
                <th className="text-left py-2">Length</th>
              </tr>
            </thead>
            <tbody>
              {SIZE_GUIDE.map((r) => (
                <tr key={r.size} className="border-b border-white/5">
                  <td className="py-2 text-jamaica-gold">{r.size}</td>
                  <td className="py-2 text-zinc-300">{r.chest}</td>
                  <td className="py-2 text-zinc-300">{r.length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </DialogContent>
      </Dialog>
    </footer>
  );
}
